import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';

import { pageTitle } from '@/components/Brand';

/**
 * Título, subtítulo e trilha do cabeçalho, definidos pela página que está na tela. A
 * página chama `usePageHeader` e o `AppLayout` desenha — o mesmo título vira o da aba.
 */
export interface PageHeader {
  title: string;
  subtitle?: string;
  crumb?: { label: string; to: string };
}

/**
 * O título fica fora do estado do `AppLayout`: só o `Header` assina. Antes, cada letra do
 * Nome no perfil (o título acompanha o rascunho) redesenhava a moldura inteira — menu,
 * avisos, camadas globais.
 */
export interface PageHeaderStore {
  get: () => PageHeader;
  set: (header: PageHeader) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createPageHeaderStore(): PageHeaderStore {
  let current: PageHeader = { title: '' };
  const listeners = new Set<() => void>();

  return {
    get: () => current,
    set: (header) => {
      current = header;
      listeners.forEach((l) => l());
    },
    subscribe: (listener) => {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
  };
}

/** O cabeçalho da página que está na tela (o `Header` da moldura). */
export function usePageHeaderValue(store: PageHeaderStore): PageHeader {
  return useSyncExternalStore(store.subscribe, store.get);
}

export const PageHeaderContext = createContext<(header: PageHeader) => void>(() => {});

export function usePageHeader(header: PageHeader): void {
  const setHeader = useContext(PageHeaderContext);
  const { title, subtitle, crumb } = header;
  const crumbLabel = crumb?.label;
  const crumbTo = crumb?.to;

  useEffect(() => {
    setHeader({
      title,
      subtitle,
      crumb: crumbLabel && crumbTo ? { label: crumbLabel, to: crumbTo } : undefined,
    });
  }, [setHeader, title, subtitle, crumbLabel, crumbTo]);

  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(title);

    return () => {
      document.title = previous;
    };
  }, [title]);
}
